import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'rc-slider';
import styles from './VideoVoiceoversControls.module.scss';
import TimeInput from '../../../components/TimeInput/TimeInput';
import { Icon } from '../../../components/Icon';
import controlBgImage from '../VideoTrimmingControls/controlBgImage.svg';
import position from '../VideoTrimmingControls/position.svg';
import Waveform from '../../../components/Waveform/Waveform';
import FloatingContainer from '../VideoTrimmingControls/FloatingContainer';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../common/components/Spinner';
import cx from 'classnames';
import { CustomButton } from '../../../components/Button/Button';
import { MIN_VOICEOVER_DURATION } from '../../../constants/voiceovers';
import { useVoiceoverContext } from '../../../context/VoiceoverContext';
import { CustomButtonTooltip } from '../../../components/ButtonTooltip/ButtonTooltip';
import Duration from '../../../components/Player/Duration';
import { useVideoProgressContext } from '../../../context/VideoProgressContext';

const POSITION_TYPES = {
  start: 0,
  end: 1,
};

const deviation = 100; // value in ms

const VideoVoiceoversControls = ({ story, videoDuration, waveform = null, className }) => {
  const {
    isVoiceoverRecording,
    changeIsVoiceoverRecording,
    isVoiceoverCountdown,
    changeIsVoiceoverCountdown,
    voiceovers,
    selectedVoiceover,
    changeVoiceovers,
    changeSelectedVoiceover,
    voiceoversChanged,
    removeVoiceover,
    isVoiceoversChanged,
    isAudioPermissionAllowed,
    updateVoiceoverSeekValue,
    voiceoverSeekValue,
    isVoiceoversLoading,
  } = useVoiceoverContext();

  const { videoProgress } = useVideoProgressContext();
  const { currentPart, videoParts, updatedStoryScenes } = useVoiceoverContext();

  // Action with the left or right slider selected
  const [typePosition, setTypePosition] = useState(POSITION_TYPES.start);

  const [showSeekTo, setShowSeekTo] = useState(false); // to show current cursor position
  const [pointerPosition, setPointerPosition] = useState(0); // to show current cursor position
  const [isRangeChanging, setIsRangeChanging] = useState(false); // to hide opacity cursor when changing

  const marksObject = useMemo(() => {
    const result = {};
    videoParts.forEach((item, index, array) => {
      const nextPoint = array[index + 1]?.startPosition ?? 1;
      const isActive = currentPart?.id === item.id;
      const isPrevActive = index - 1 >= 0 && currentPart?.id === array[index - 1]?.id;
      const isNextActive = index + 1 < array.length && currentPart?.id === array[index + 1]?.id;
      let borderWidth = '1px 0.5px';
      if (isActive) {
        borderWidth = '1px';
      } else if (isPrevActive) {
        borderWidth = '1px 0.5px 1px 0px';
      } else if (isNextActive) {
        borderWidth = '1px 0px 1px 0.5px';
      }
      result[parseFloat(item.startPosition).toFixed(4)] = {
        style: {
          width: `${(nextPoint - item.startPosition) * 100}%`,
          height: '28px',
          backgroundColor: 'none',
          borderWidth,
          borderStyle: 'solid',
          borderColor: isActive ? '#7C43AA' : 'rgb(189, 189, 189)',
          top: '-50px',
          transform: 'translateX(0) !important',
          fontFamily: `'Nunito Sans', sans-serif`,
          fontSize: '12px',
          overflow: 'hidden',
          padding: '4px 0px',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          userSelect: 'none',
          transition: 'border-color 250ms ease-in',
          ...(index === 0 ? { borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' } : {}),
          ...(index === array.length - 1 ? { borderTopRightRadius: '8px', borderBottomRightRadius: '8px' } : {}),
        },
        label: item.name,
      };
    });
    return result;
  }, [currentPart, videoParts])

  // Show start or end time
  const [handlersPositions, setHandlersPositions] = useState([0, 1]);

  const [isDragActionActive, setIsDragActionActive] = useState(false);
  const [startDragPosition, setStartDragPosition] = useState(0); // [0, 1]

  const [initialDragOffset, setInitialDragOffset] = useState(0);
  const [isDragInvalid, setIsDragInvalid] = useState(false);

  const [temporarySeekValue, setTemporarySeekValue] = useState(null);
  const [isDragged, setIsDragged] = useState(false);

  const [prevRangeValue, setPrevRangeValue] = useState({ startTime: 0, endTime: 0 });

  const [startRecordingTime, setStartRecordingTime] = useState(0);

  const recordedDuration = (videoProgress?.milliseconds / 1000 - startRecordingTime / 1000)?.toFixed(2); // value in seconds

  const isDisableRecording = useMemo(() => {
    const isUnavailableBeforeEnd = videoDuration - videoProgress?.milliseconds <= deviation;

    const currentVoiceover =
      voiceovers.find(
        ({ storyOffset, trimEndOffset }) =>
          videoProgress?.milliseconds >= storyOffset &&
          videoProgress?.milliseconds <= storyOffset + trimEndOffset
      ) || {};

    if (currentVoiceover?.id) {
      const isUnavailableBeforeEnd = videoDuration - (currentVoiceover?.storyOffset + currentVoiceover?.trimEndOffset) < deviation;

      const isUnavailableSpaceAfterRange = voiceovers?.sort((a, b) => a.storyOffset - b.storyOffset)?.some((currentItem, index, arr) => {
        if (currentVoiceover?.id === currentItem?.id) {
          const nextRange = arr[index + 1];
          if (nextRange) {
            return (
              nextRange?.storyOffset + nextRange?.trimStartOffset - (currentItem?.storyOffset + currentItem?.trimEndOffset) < deviation
            );
          }
        }
      });

      return isUnavailableBeforeEnd || isUnavailableSpaceAfterRange;
    }

    return isUnavailableBeforeEnd;
  }, [voiceovers, videoProgress?.milliseconds, videoDuration]);

  const getVoiceoversRange = items =>
    items.map(({ id, storyOffset, trimStartOffset, trimEndOffset }) => ({ id, startTime: storyOffset + trimStartOffset, endTime: storyOffset + trimEndOffset }));

  const currentStartTime = useMemo(() => {
    const { trimStartOffset, storyOffset } = voiceovers?.find(voiceover => voiceover.id === selectedVoiceover.id) || {};
    return storyOffset + trimStartOffset;
  }, [voiceovers, selectedVoiceover.id]);

  const currentEndTime = useMemo(() => {
    const { storyOffset, trimEndOffset } = voiceovers?.find(voiceover => voiceover.id === selectedVoiceover.id) || {};
    return storyOffset + trimEndOffset;
  }, [voiceovers, selectedVoiceover.id]);

  const { t } = useTranslation();

  const voiceoverRef = useRef(null);

  const updateActionTimestamp = useCallback((id, startPosition, endPosition) => {
    const offset = startPosition * videoDuration;
    const duration = endPosition * videoDuration - offset;

    const isStartTrimChanged = endPosition === prevRangeValue.endTime && startPosition !== prevRangeValue.startTime;

    if (duration < MIN_VOICEOVER_DURATION) {
      return;
    }

    voiceoversChanged(true);

    const sortedByStartTime = voiceovers.sort((a, b) => (a.storyOffset + a.trimStartOffset) - (b.storyOffset + b.trimStartOffset));
    const currentIndex = sortedByStartTime.map(({ id }) => id).indexOf(id);

    const currentItem = sortedByStartTime[currentIndex];

    const otherVoiceovers = sortedByStartTime.filter(voiceover => voiceover.id !== id);
    const wouldOverlap = otherVoiceovers.some(voiceover => {
      const voiceoverStart = voiceover.storyOffset + voiceover.trimStartOffset;
      const voiceoverEnd = voiceover.storyOffset + voiceover.trimEndOffset;

      const newStart = isStartTrimChanged ? offset : currentItem.storyOffset + currentItem.trimStartOffset;
      const newEnd = isStartTrimChanged ?
        currentItem.storyOffset + currentItem.trimEndOffset :
        currentItem.storyOffset + currentItem.trimStartOffset + duration;

      const isStartInside = voiceoverStart <= newStart && newStart <= voiceoverEnd;
      const isEndInside = voiceoverStart <= newEnd && newEnd <= voiceoverEnd;
      const isVoiceoverInside = newStart <= voiceoverStart && voiceoverEnd <= newEnd;

      return isStartInside || isEndInside || isVoiceoverInside;
    });

    if (wouldOverlap) {
      return;
    }

    if (isStartTrimChanged) {
      const leftClosest = sortedByStartTime[currentIndex - 1];
      const leftCheck = leftClosest && leftClosest.storyOffset + leftClosest.trimEndOffset >= offset;

      const isLessThanStoryOffset = currentItem.storyOffset - offset > 0;

      if (isLessThanStoryOffset) {
        changeVoiceovers(id, { trimStartOffset: 0 });
        return;
      }

      if (leftCheck) {
        changeVoiceovers(id, { trimStartOffset: (leftClosest.storyOffset + leftClosest.trimEndOffset) - currentItem.storyOffset + 1 });
        return;
      }

      changeVoiceovers(id, { trimStartOffset: offset - currentItem.storyOffset });

    } else {
      const isMoreThanDuration = (offset + duration) - (currentItem.storyOffset + currentItem.duration) > 0;

      const rightClosest = sortedByStartTime[currentIndex + 1];
      const rightCheck = rightClosest && offset + duration >= rightClosest.storyOffset + rightClosest.trimStartOffset;

      if (isMoreThanDuration) {
        changeVoiceovers(id, { trimEndOffset: currentItem.duration });
        return;
      }

      if (rightCheck) {
        changeVoiceovers(id, { trimEndOffset: (rightClosest.storyOffset + rightClosest.trimStartOffset) - currentItem.storyOffset - 1 });
        return;
      }

      changeVoiceovers(id, { trimEndOffset: duration + currentItem.trimStartOffset });
    }

    setIsRangeChanging(true);
  }, [prevRangeValue, changeVoiceovers, voiceovers, videoDuration, voiceoversChanged]);

  const onSeekChange = useCallback(
    rangeValue => {
      console.log('onSeekChange', rangeValue);
      const currentTime = rangeValue * videoDuration;
      const currentVoiceover = voiceovers.find(
        ({ trimStartOffset, storyOffset, trimEndOffset }) =>
          storyOffset + trimStartOffset <= currentTime && currentTime <= storyOffset + trimEndOffset
      );

      /**
       * After adding the drag functionality directly to the voiceover range element,
       * we need to determine whether it was just a click and release of the mouse without moving it or not
       * If there was a normal mouse click, set a new voiceoverSeekValue.
       * If there was a voiceover movement, we ignore setting the value for voiceoverSeekValue.
       * This is done to separate these two voiceovers.
       */
      if (currentVoiceover && !voiceoverRef.current.isHandler && !voiceoverRef.current.isMark) {
        changeSelectedVoiceover(currentVoiceover);
        // This value will be set to updateVoiceoverSeekValue() after releasing the mouse button if there was no voiceover movement
        setTemporarySeekValue(rangeValue);
        startDragActive({ rangeValue, voiceoverToMove: currentVoiceover });
      } else {
        let value = rangeValue;
        // If mark clicked, make sure to seek to the scene start precisely
        if (voiceoverRef.current.isMark) {
          const sceneToSelect = videoParts.find(({ startPosition }) => startPosition === value);
          if (sceneToSelect) {
            value = (sceneToSelect?.start) / videoDuration;
          }
          // If voiceoverSeekValue is equal to the value, we need to add a small value to avoid the same value
          // so useEffect in player will detect changes in voiceoverSeekValue and seek to the value 
          if (voiceoverSeekValue === value) {
            value = voiceoverSeekValue + 0.0001;
          }
        }
        updateVoiceoverSeekValue(value);
      }
    },
    [updateVoiceoverSeekValue, voiceovers, videoDuration, isVoiceoversChanged, selectedVoiceover, voiceoverSeekValue]
  );

  const handleOnMouseMove = e => {
    const cursorPosition = e.clientX - voiceoverRef.current.offsetLeft - voiceoverRef.current.offsetParent.offsetLeft;
    setPointerPosition(cursorPosition - 6);
  };

  const handleMouseDown = e => {
    voiceoverRef.current.isHandler = Array.from(e?.target?.classList).indexOf('rc-slider-handle') >= 0;
    voiceoverRef.current.isMark = Array.from(e?.target?.classList).indexOf('rc-slider-mark-text') >= 0;

    const tabIndex = e.target.tabIndex;

    if (tabIndex >= 0) {
      setTypePosition(tabIndex);
    }
  };

  const startDragActive = ({ event, rangeValue, voiceoverToMove }) => {
    setIsDragActionActive(true);

    /**
     * Voiceover movements are available in two ways:
     * 1) Via an icon outside the slider
     * In this case, we use the current position of the mouse
     * 2) By clicking on the voiceovers slider itself directly
     * In this case, we use the current range value
     * */

    if (event) {
      setStartDragPosition(event.clientX - voiceoverRef.current.offsetLeft - voiceoverRef.current.offsetParent.offsetLeft);
      setIsDragged(true);
    }

    if (rangeValue) {
      setStartDragPosition(voiceoverRef.current.offsetWidth * rangeValue);
    }

    const currentVoiceover = voiceovers.find(({ id }) => id === voiceoverToMove.id) || {};

    setInitialDragOffset(currentVoiceover.storyOffset);
  };

  const stopDragActive = () => {
    setIsDragActionActive(false);
    setStartDragPosition(0);

    if (isDragInvalid) {
      changeVoiceovers(selectedVoiceover.id, { storyOffset: initialDragOffset });
    }

    if (isDragged && !isDragInvalid) {
      voiceoversChanged(true);
    }

    if (!isDragged || voiceoverRef?.current?.isHandler) {
      //There was no voiceover movement - set temporarySeekValue
      updateVoiceoverSeekValue(temporarySeekValue);
    }

    setIsDragged(false);
    setIsDragInvalid(false);
  };

  const voiceoverDragListener = event => {
    setIsDragged(true);

    const currentDragPosition = event.clientX - voiceoverRef.current.offsetLeft - voiceoverRef.current.offsetParent.offsetLeft;

    const minAvailableValue = 0;
    const maxAvailableValue = voiceoverRef.current.offsetWidth;

    const rangeInPercents = (currentDragPosition - startDragPosition) / maxAvailableValue;

    const { storyOffset, trimStartOffset, trimEndOffset } = voiceovers.find(voiceover => voiceover.id === selectedVoiceover.id) || {};

    let newOffset = storyOffset + trimStartOffset + videoDuration * rangeInPercents;

    if (currentDragPosition <= minAvailableValue) {
      newOffset = 0;
    }

    if (currentDragPosition >= maxAvailableValue) {
      newOffset = videoDuration - trimEndOffset;
    }

    if (newOffset < 0 || newOffset + trimEndOffset > videoDuration) {
      return;
    }

    const actionsRange = getVoiceoversRange(voiceovers.filter(voiceover => voiceover.id !== selectedVoiceover.id));

    const isInvalidTimestamps = actionsRange.some(({ startTime, endTime }) => {
      const currentDuration = trimEndOffset - trimStartOffset;
      const isStartInside = startTime < newOffset && newOffset < endTime;
      const isEndInside = startTime < newOffset + currentDuration && newOffset + currentDuration < endTime;

      const isInsideAction = newOffset < startTime && endTime < newOffset + currentDuration;

      const isExactOverlap = newOffset === endTime || newOffset + currentDuration === startTime;

      return (isStartInside || isEndInside || isInsideAction) && !isExactOverlap;
    });

    changeVoiceovers(selectedVoiceover.id, { storyOffset: newOffset - trimStartOffset });

    setIsDragInvalid(isInvalidTimestamps);
  };

  const handleDeleteAction = async () => {
    const isDeletionConfirmed = confirm(t('call_to_action.editor.delete_confirmation'));
    if (!isDeletionConfirmed) {
      return;
    }

    removeVoiceover(selectedVoiceover?.id);
    voiceoversChanged(true);
  };

  const trackLeftRightBorder = useCallback(
    voiceoverId => {
      if (isDragged && selectedVoiceover.id === voiceoverId) {
        return isDragInvalid ? '1px solid red' : '1px solid #7C43AA';
      }

      if (selectedVoiceover.id !== voiceoverId) {
        return '1px solid #7C43AA';
      }

      if (isDragged) {
        return '1px solid #7C43AA';
      }

      return 'none';
    },
    [selectedVoiceover, isDragInvalid, isDragged]
  );

  const handleStopRecording = () => {
    if (isVoiceoverCountdown) {
      changeIsVoiceoverCountdown(false);
      return;
    }

    changeIsVoiceoverRecording(false);
  };

  useEffect(() => {
    setHandlersPositions([currentStartTime / videoDuration, currentEndTime / videoDuration]);
  }, [voiceovers, currentStartTime, currentEndTime, videoDuration, selectedVoiceover.id]);

  useEffect(() => {
    if (isDragActionActive) {
      document.addEventListener('mouseup', stopDragActive);
    }

    return async () => {
      document.removeEventListener('mouseup', stopDragActive);
    };
  }, [isDragInvalid, initialDragOffset, isDragActionActive, isDragged]);

  useEffect(() => {
    if (isDragActionActive) {
      document.addEventListener('mousemove', voiceoverDragListener);
    }

    return () => document.removeEventListener('mousemove', voiceoverDragListener);
  }, [isDragActionActive]);

  useEffect(() => {
    if (isVoiceoverRecording) {
      setStartRecordingTime(videoProgress?.milliseconds || 0);
    }
  }, [isVoiceoverRecording]);

  useEffect(() => {
    const body = document.querySelector('body');
    body.addEventListener('mousedown', handleMouseDown);

    updatedStoryScenes(story);

    return () => {
      body.removeEventListener('mousedown', handleMouseDown);
      updatedStoryScenes(null);
    };
  }, []);

  return (
    <>
      <div className={styles.controlBtnContainer}>
        {isVoiceoverRecording || isVoiceoverCountdown ? (
          <>
            {isVoiceoverRecording && <Duration className={styles.elapsedTime} seconds={recordedDuration} />}
            <CustomButtonTooltip className={styles.actionBtn} label={t('voice_over.controls.stop')} variant="primary" onClick={handleStopRecording} />
          </>
        ) : (
          <>
            <CustomButton
              label={t('general.delete')}
              variant="secondary"
              onClick={handleDeleteAction}
              className={styles.deleteBtn}
              disabled={!selectedVoiceover?.id}
            />
            {isAudioPermissionAllowed && (
              <CustomButton
                label={t('voice_over.controls.record')}
                variant="primary"
                onClick={() => changeIsVoiceoverCountdown(true)}
                disabled={isDisableRecording || isVoiceoversLoading}
                tooltip={t('voice_over.controls.no_space_available')}
              />
            )}
          </>
        )}
      </div>
      <div
        className={cx(styles.voiceoversInstruments, className, {
          [styles.disableVoiceover]: isVoiceoverRecording || isVoiceoverCountdown || isVoiceoversLoading,
        })}
        ref={voiceoverRef}
      >
        {showSeekTo && !isRangeChanging && !isDragged && (
          <div className={styles.cursor} style={{ left: `${pointerPosition}px` }}></div>
        )}
        <div
          className={styles.slidersWrapper}
          onMouseMove={handleOnMouseMove}
          onMouseOver={() => setShowSeekTo(true)}
          onMouseLeave={() => setShowSeekTo(false)}
        >
          {typePosition >= 0 && !!selectedVoiceover.id && (
            <FloatingContainer
              className={styles.dragIconContainer}
              parentRef={voiceoverRef}
              floatingValue={handlersPositions[typePosition]}
              correction={typePosition ? -7 : -15}
            >
              <Icon
                src="dragIcon"
                className={cx(styles.dragIcon, {
                  [styles.startPosition]: typePosition === POSITION_TYPES.start,
                  [styles.endPosition]: typePosition === POSITION_TYPES.end,
                })}
              />
            </FloatingContainer>
          )}

          {!!selectedVoiceover.id && (
            <FloatingContainer
              className={styles.dragAction}
              parentRef={voiceoverRef}
              floatingValue={
                handlersPositions[POSITION_TYPES.end] -
                (handlersPositions[POSITION_TYPES.end] - handlersPositions[POSITION_TYPES.start]) / 2
              }
              tooltip={t('call_to_action.editor.tooltip.drag')}
              disabled={isDragged}
            >
              <Icon
                src="dragAction"
                className={styles.dragIcon}
                onMouseDown={event => startDragActive({ event, voiceoverToMove: selectedVoiceover })}
              />
            </FloatingContainer>
          )}

          {voiceovers?.length > 0 ? (
            voiceovers.map((voiceover, index) => (
              <Slider
                key={voiceover?.id}
                range
                defaultValue={[0, 1]}
                value={[
                  (voiceover.storyOffset + voiceover.trimStartOffset) / videoDuration,
                  (voiceover.storyOffset + voiceover.trimEndOffset) / videoDuration,
                ]}
                min={0}
                max={1}
                tabIndex={[0, 1]}
                step={1 / (videoDuration / 100)}
                allowCross={false}
                onChange={([startTime, endTime]) => updateActionTimestamp(voiceover.id, startTime, endTime)}
                className={cx(styles.range, { [styles.draggableRange]: isDragged })}
                trackStyle={[
                  {
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    height: '56px',
                    border: 'none',
                    borderTop: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    borderBottom: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    borderLeft: trackLeftRightBorder(voiceover.id),
                    borderRight: trackLeftRightBorder(voiceover.id),
                    zIndex: selectedVoiceover.id === voiceover.id && (isDragInvalid || isDragged) ? 52 : 3,
                    ...(selectedVoiceover.id === voiceover.id && !isDragged && { borderRadius: '0' }),
                  },
                ]}
                handleStyle={[
                  {
                    display: selectedVoiceover.id === voiceover.id && !isDragged ? 'inline-block' : 'none',
                    width: '12px',
                    height: '56px',
                    backgroundColor:
                      selectedVoiceover.id === voiceover.id && typePosition === POSITION_TYPES.start ? '#7C43AA' : '#BDBDBD',
                    backgroundImage: `url(${controlBgImage})`,
                    backgroundPosition: '50% 50%',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '0',
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                    border: 'none',
                    borderTop: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    borderLeft: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    borderBottom: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    boxShadow: 'none',
                    outline: 'none',
                    position: 'absolute',
                    marginLeft: '-12px', // 0px
                    zIndex: '51',
                    top: '10px',
                  },
                  {
                    display: selectedVoiceover.id === voiceover.id && !isDragged ? 'inline-block' : 'none',
                    width: '12px',
                    height: '56px',
                    backgroundColor:
                      selectedVoiceover.id === voiceover.id && typePosition === POSITION_TYPES.end ? '#7C43AA' : '#BDBDBD',
                    backgroundImage: `url(${controlBgImage})`,
                    backgroundPosition: '50% 50%',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '0',
                    borderTopRightRadius: '4px',
                    borderBottomRightRadius: '4px',
                    border: 'none',
                    borderTop: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    borderRight: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    borderBottom: selectedVoiceover.id === voiceover.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                    boxShadow: 'none',
                    outline: 'none',
                    position: 'absolute',
                    top: '10px',
                    right: '0',
                    marginLeft: '0px', // -12px
                    zIndex: '51',
                  },
                ]}
                railStyle={{
                  height: '56px',
                  border: '1px solid #BDBDBD',
                  background: index === 0 ? '#EEEEEE' : 'none',
                  borderRadius: '4px',
                  boxShadow: 'none',
                }}
                onBeforeChange={([startTime, endTime]) => setPrevRangeValue({ startTime, endTime })}
                onAfterChange={() => setIsRangeChanging(false)}
              />
            ))
          ) : (
            <Slider
              range
              defaultValue={[0, 1]}
              min={0}
              max={1}
              tabIndex={[0, 1]}
              step={1 / (videoDuration / 100)}
              allowCross={false}
              className={styles.range}
              trackStyle={[
                {
                  display: 'none',
                },
              ]}
              handleStyle={[
                {
                  display: 'none',
                },
                {
                  display: 'none',
                },
              ]}
              railStyle={{
                height: '56px',
                border: '1px solid #BDBDBD',
                background: '#EEEEEE',
                borderRadius: '4px',
                boxShadow: 'none',
              }}
            />
          )}

          {isVoiceoverRecording && (
            <Slider
              range
              defaultValue={[0, 1]}
              value={[
                startRecordingTime / videoDuration,
                videoProgress?.milliseconds / videoDuration,
              ]}
              min={0}
              max={1}
              tabIndex={[0, 1]}
              step={1 / (videoDuration / 100)}
              allowCross={false}
              className={styles.range}
              trackStyle={[
                {
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  height: '56px',
                  border: 'none',
                  borderTop: '1px solid #7C43AA',
                  borderBottom: '1px solid #7C43AA',
                  borderLeft: '1px solid #7C43AA',
                  borderRight: '1px solid #7C43AA',
                  zIndex: 3,
                },
              ]}
              handleStyle={[
                {
                  display: 'none',
                },
                {
                  display: 'none',
                },
              ]}
              railStyle={{
                height: '56px',
                border: '1px solid #BDBDBD',
                background: 'none',
                borderRadius: '4px',
                boxShadow: 'none',
              }}
            />
          )}

          <Slider
            marks={marksObject}
            className={styles.videoProgress}
            trackStyle={{
              backgroundColor: 'rgba(124, 67, 170, 0.1)',
              height: '56px',
              top: '30%',
              border: '0px',
              zIndex: 3,
            }}
            handleStyle={{
              backgroundImage: `url(${position})`,
              backgroundPosition: '50% 0%',
              borderRadius: '0',
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
              cursor: 'default',
              backgroundRepeat: 'no-repeat',
              backgroundColor: 'transparent',
              zIndex: '50',
              height: '63px',
              top: '2px',
            }}
            railStyle={{
              height: '58px',
              background: 'transparent',
              zIndex: '50',
              boxShadow: 'none',
            }}
            min={0}
            max={1}
            step={0.001}
            onChange={onSeekChange}
            value={videoProgress.percents}
            dotStyle={{
              display: 'none',
            }}
            included
          />

          {waveform ? (
            <div className={styles.waveformContainer}>
              <Waveform
                waveformData={waveform.map(item => item * 10000)}
                parentRef={voiceoverRef}
                trimStartPos={0}
                progress={videoProgress.percents}
              />
            </div>
          ) : (
            <Spinner width="30px" height="30px" size="3px" className={styles.spinnerIcon} />
          )}
          <FloatingContainer
            parentRef={voiceoverRef}
            floatingValue={videoProgress.percents}
            correction={8}
            className={styles.progressContainer}
          >
            <TimeInput
              inputClassName={styles.timeInput}
              disabled={true}
              value={(videoProgress.percents * videoDuration) / 1000}
              variant="basic"
              showControls={false}
            />
          </FloatingContainer>
          {typePosition >= 0 && !!selectedVoiceover.id && (
            <FloatingContainer
              className={styles.voiceoversContainer}
              parentRef={voiceoverRef}
              floatingValue={handlersPositions[typePosition]}
            >
              {typePosition === 0 ? (
                <TimeInput
                  inputClassName={styles.timeInput}
                  disabled={true}
                  value={currentStartTime / 1000}
                  variant="basic"
                  showControls={false}
                />
              ) : (
                <TimeInput
                  className={styles.timeInput}
                  disabled={true}
                  value={currentEndTime / 1000}
                  variant="basic"
                  showControls={false}
                />
              )}
              <div className={styles.keyHint}>
                <span>{t('my_video.tools.trim.use')}</span>
                <Icon src="keyArrowLeft" className={styles.keyIcon} />
                <Icon src="keyArrowRight" className={styles.keyIcon} />
              </div>
            </FloatingContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoVoiceoversControls;
